import { useState, useEffect } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import InputField from 'components/fields/InputField';
import SelectField from 'components/fields/SelectField';
import ButtonSelectField from 'components/fields/ButtonSelectField';
import MultiSelectField from 'components/fields/MultiSelectField';
import { toast } from 'react-toastify';

// FormBuilder field definition type
interface FormBuilderField {
    id: string;
    name: string;
    type: 'string' | 'select' | 'multi-select';
    required: boolean;
    options?: string[];
}

// Form data type
interface FormData {
    [key: string]: string | string[];
}

interface FormTemplateStyle {
    borderColor?: string;
    borderWidth?: string;
    borderRadius?: string;
    backgroundColor?: string;
    textColor?: string;
    labelColor?: string;
    buttonBackgroundColor?: string;
    buttonTextColor?: string;
    inputBackgroundColor?: string;
    inputBorderColor?: string;
    inputTextColor?: string;
    fontFamily?: string;
    fontSize?: string;
    ctaText?: string;
}

interface LeadFormProps {
    sellerId?: string;
    offerId?: string;
    templateId?: string;
    sideSellerId?: string;
    isSideSellerMode?: boolean;
    offerFields?: string[]; // Array of field IDs that should be included in the form
    formBuilder?: { fields: FormBuilderField[] }; // Optional custom formBuilder config
    templateStyle?: FormTemplateStyle; // Optional template styling
    onSuccess?: () => void;
    onError?: (error: any) => void;
}

// Default formBuilder configuration - moved outside component
const defaultFormBuilder = {
    fields: [
        { id: 'firstName', name: 'Prénom', type: 'string' as const, required: true },
        { id: 'lastName', name: 'Nom', type: 'string' as const, required: true },
        { id: 'phone', name: 'Téléphone', type: 'string' as const, required: true },
        { id: 'email', name: 'Email', type: 'string' as const, required: true },
        { id: 'address', name: 'Adresse', type: 'string' as const, required: false },
        { id: 'department', name: 'Département', type: 'string' as const, required: true },
    ],
};

const LeadForm = ({
    sellerId,
    offerId,
    templateId,
    sideSellerId,
    isSideSellerMode = false,
    offerFields = [],
    formBuilder,
    templateStyle,
    onSuccess,
    onError,
}: LeadFormProps) => {
    const [formData, setFormData] = useState<FormData>({});
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [availableFields, setAvailableFields] = useState<FormBuilderField[]>([]);

    // Apply default template styles
    const style = {
        borderColor: templateStyle?.borderColor || '#e5e7eb',
        borderWidth: templateStyle?.borderWidth || '1px',
        borderRadius: templateStyle?.borderRadius || '12px',
        backgroundColor: templateStyle?.backgroundColor || '#ffffff',
        textColor: templateStyle?.textColor || '#1f2937',
        labelColor: templateStyle?.labelColor || '#374151',
        buttonBackgroundColor: templateStyle?.buttonBackgroundColor || '#4f46e5',
        buttonTextColor: templateStyle?.buttonTextColor || '#ffffff',
        inputBackgroundColor: templateStyle?.inputBackgroundColor || '#ffffff',
        inputBorderColor: templateStyle?.inputBorderColor || '#d1d5db',
        inputTextColor: templateStyle?.inputTextColor || '#1f2937',
        fontFamily: templateStyle?.fontFamily || 'inherit',
        fontSize: templateStyle?.fontSize || '16px',
        ctaText: templateStyle?.ctaText || 'Envoyer ma demande',
    };

    useEffect(() => {
        // Use provided formBuilder or default one
        const builder: any = formBuilder || defaultFormBuilder;

        // Filter fields based on offerFields if provided
        let filtered: FormBuilderField[];
        if (offerFields && offerFields.length > 0) {
            filtered = builder.fields.filter((field: any) =>
                offerFields.includes(field.id)
            );
            console.log('Builder',  builder.fields);
            console.log('offerFields:', offerFields);
        } else {
            // If no offerFields specified, use all fields
            filtered = builder.fields;
        }

        // Sort fields to prioritize Prénom, Nom, Email, Code postal in that order
        const priorityOrder = ['Prénom', 'Nom', 'Email', 'Code postal'];
        const sortedFields = [...filtered].sort((a, b) => {
            const aIndex = priorityOrder.indexOf(a.id);
            const bIndex = priorityOrder.indexOf(b.id);
            
            // If both fields are in priority list, sort by priority order
            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }
            // If only a is in priority list, a comes first
            if (aIndex !== -1) {
                return -1;
            }
            // If only b is in priority list, b comes first
            if (bIndex !== -1) {
                return 1;
            }
            // Neither is in priority list, maintain original order
            return 0;
        });

        setAvailableFields(sortedFields);
    }, [offerFields, formBuilder]);

    useEffect(() => {
        console.log('Available fields for the form:', availableFields);
}, [availableFields]);

    const handleInputChange = (fieldId: string, value: string | string[]) => {
        setFormData((prev) => ({
            ...prev,
            [fieldId]: value,
        }));

        // Clear error when user starts typing
        if (errors[fieldId]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[fieldId];
                return newErrors;
            });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        availableFields.forEach((field) => {
            if (field.required) {
                const value = formData[field.id];
                if (!value || (Array.isArray(value) && value.length === 0)) {
                    newErrors[field.id] = `${field.name} est requis`;
                }
            }
        });

        // Email validation if present
        if (formData.email && typeof formData.email === 'string') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = 'Email invalide';
            }
        }

        // Phone validation if present
        if (formData.phone && typeof formData.phone === 'string') {
            const phoneRegex = /^[0-9\s\-+()]{10,}$/;
            if (!phoneRegex.test(formData.phone)) {
                newErrors.phone = 'Numéro de téléphone invalide';
            }
        }

        // Postal code validation if present (French postal code: 5 digits)
        const postalCodeField = availableFields.find(f => f.id === 'Code postal' || f.name === 'Code postal');
        if (postalCodeField && formData[postalCodeField.id] && typeof formData[postalCodeField.id] === 'string') {
            const postalCode = formData[postalCodeField.id] as string;
            const postalCodeRegex = /^\d{5}$/;
            if (!postalCodeRegex.test(postalCode.trim().replace(/\s/g, ''))) {
                newErrors[postalCodeField.id] = 'Code postal invalide (5 chiffres requis)';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Veuillez corriger les erreurs dans le formulaire');
            return;
        }

        setIsSubmitting(true);

        try {
            // Format data for the API - convert to the expected format
            const formattedData = Object.entries(formData).reduce((acc, [key, value]) => {
                acc[key] = Array.isArray(value) ? value.join(', ') : value;
                return acc;
            }, {} as { [key: string]: string });

            // Send to appropriate endpoint based on mode
            let response;
            if (isSideSellerMode && templateId) {
                // Side-seller mode: use generic-form endpoint
                const url = `${CONFIG.apiUrl}/lead/public/generic-form/${templateId}${
                    sideSellerId ? `?sideSellerId=${sideSellerId}` : ''
                }`;
                response = await axios.post(url, formattedData);
            } else if (sellerId && offerId) {
                // Regular mode: use seller/offer endpoint
                response = await axios.post(
                    `${CONFIG.apiUrl}/lead/${sellerId}/${offerId}`,
                    formattedData
                );
            } else {
                throw new Error('Missing required parameters for lead submission');
            }

            if (response.status === 200 && response.data.type === 'success') {
                setFormData({});
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                throw new Error(response.data.status || "Erreur lors de l'envoi du lead");
            }
        } catch (error: any) {
            console.error('Error submitting lead:', error);
            toast.error(
                error.response?.data?.status ||
                    error.message ||
                    "Erreur lors de l'envoi du lead"
            );
            if (onError) {
                onError(error);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderField = (field: FormBuilderField) => {
        const fieldState = errors[field.id] ? 'error' : undefined;
        const hasValue = formData[field.id] && (
            Array.isArray(formData[field.id]) 
                ? formData[field.id].length > 0 
                : formData[field.id] !== ''
        );

        // Determine if this field should use button-style selection
        const useButtonStyle = field.type === 'select' && field.options && field.options.length <= 4;

        switch (field.type) {
            case 'select':
                if (useButtonStyle) {
                    return (
                        <div className="relative">
                            <ButtonSelectField
                                key={field.id}
                                id={field.id}
                                label={field.name}
                                options={field.options || []}
                                value={formData[field.id] as string}
                                onChange={(value: string) =>
                                    handleInputChange(field.id, value)
                                }
                                required={field.required}
                                state={fieldState}
                                extra="transition-all duration-200"
                                columns={field.options!.length === 2 ? 2 : field.options!.length === 3 ? 3 : 4}
                            />
                        </div>
                    );
                }
                return (
                    <div className="relative">
                        <SelectField
                            key={field.id}
                            id={field.id}
                            label={field.name}
                            placeholder={`Sélectionnez ${field.name.toLowerCase()}`}
                            options={field.options || []}
                            value={formData[field.id] as string}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                handleInputChange(field.id, e.target.value)
                            }
                            required={field.required}
                            state={fieldState}
                            extra="transition-all duration-200"
                        />
                        {hasValue && (
                            <div className="absolute right-3 top-11 pointer-events-none">
                                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                        )}
                    </div>
                );

            case 'multi-select':
                return (
                    <MultiSelectField
                        key={field.id}
                        id={field.id}
                        label={field.name}
                        options={field.options || []}
                        value={formData[field.id] as string[]}
                        onChange={(selectedOptions) =>
                            handleInputChange(field.id, selectedOptions)
                        }
                        required={field.required}
                        state={fieldState}
                        extra="transition-all duration-200"
                    />
                );

            case 'string':
            default:
                return (
                    <div className="relative">
                        <InputField
                            key={field.id}
                            id={field.id}
                            label={field.name}
                            placeholder={
                                field.id === 'Code postal' || field.name === 'Code postal'
                                    ? 'Ex: 75001'
                                    : `Entrez ${field.name.toLowerCase()}`
                            }
                            type={
                                field.id === 'email' || field.id === 'Email'
                                    ? 'email'
                                    : field.id === 'phone' || field.id === 'Téléphone'
                                    ? 'tel'
                                    : field.id === 'Code postal' || field.name === 'Code postal'
                                    ? 'text'
                                    : 'text'
                            }
                            value={formData[field.id] as string}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleInputChange(field.id, e.target.value)
                            }
                            state={fieldState}
                            extra="transition-all duration-200"
                        />
                        {hasValue && !errors[field.id] && (
                            <div className="absolute right-3 top-11 pointer-events-none animate-scale-in">
                                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                        )}
                    </div>
                );
        }
    };

    return (
        <form 
            onSubmit={handleSubmit} 
            className="w-full p-6 rounded-2xl"
            style={{
                backgroundColor: style.backgroundColor,
                borderColor: style.borderColor,
                borderWidth: style.borderWidth,
                borderRadius: style.borderRadius,
                borderStyle: 'solid',
                fontFamily: style.fontFamily,
                fontSize: style.fontSize,
                color: style.textColor,
            }}
        >
            {/* Progress indicator */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold" style={{ color: style.textColor }}>
                        Quelques informations pour continuer
                    </span>
                    <span 
                        className="text-xs font-medium px-3 py-1 rounded-full"
                        style={{
                            color: style.buttonBackgroundColor,
                            backgroundColor: `${style.buttonBackgroundColor}15`,
                        }}
                    >
                        1 minute
                    </span>
                </div>
                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div 
                        className="h-full rounded-full transition-all duration-500 ease-out"
                        style={{ 
                            width: `${(Object.keys(formData).length / availableFields.length) * 100}%`,
                            background: `linear-gradient(to right, ${style.buttonBackgroundColor}, ${style.buttonBackgroundColor}dd)`,
                        }}
                    />
                </div>
            </div>

            <div className="space-y-5">
                {availableFields.map((field, index) => {
                    // Check if this is Prénom and next field is Nom for side-by-side layout
                    const isPrenomField = field.name === 'Prénom' || field.id === 'Prénom';
                    const nextField = availableFields[index + 1];
                    const nextIsNomField = nextField && (nextField.name === 'Nom' || nextField.id === 'Nom');
                    const isNomField = field.name === 'Nom' || field.id === 'Nom';
                    const prevField = availableFields[index - 1];
                    const prevIsPrenomField = prevField && (prevField.name === 'Prénom' || prevField.id === 'Prénom');

                    // Skip rendering Nom here if previous was Prénom (it will be rendered alongside Prénom)
                    if (isNomField && prevIsPrenomField) {
                        return null;
                    }

                    // If this is Prénom and next is Nom, render them side by side
                    if (isPrenomField && nextIsNomField) {
                        return (
                            <div
                                key={`${field.id}-${nextField.id}`}
                                className="grid grid-cols-2 gap-4 group transform transition-all duration-300 ease-out"
                                style={{
                                    animation: `slideInField 0.4s ease-out ${index * 0.05}s both`
                                }}
                            >
                                <div>
                                    {renderField(field)}
                                    {errors[field.id] && (
                                        <div className="mt-2 flex items-start gap-2 text-sm text-red-600 animate-shake">
                                            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span className="font-medium">{errors[field.id]}</span>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    {renderField(nextField)}
                                    {errors[nextField.id] && (
                                        <div className="mt-2 flex items-start gap-2 text-sm text-red-600 animate-shake">
                                            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span className="font-medium">{errors[nextField.id]}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }
                    
                    // Regular single field rendering
                    return (
                        <div
                            key={field.id}
                            className="group transform transition-all duration-300 ease-out"
                            style={{
                                animation: `slideInField 0.4s ease-out ${index * 0.05}s both`
                            }}
                        >
                            {renderField(field)}
                            {errors[field.id] && (
                                <div className="mt-2 flex items-start gap-2 text-sm text-red-600 animate-shake">
                                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-medium">{errors[field.id]}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Submit Button - Premium Design */}
            <div className="mt-8 space-y-4">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full overflow-hidden rounded-2xl px-8 py-4 text-lg font-bold shadow-xl transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
                    style={{
                        background: style.buttonBackgroundColor,
                        color: style.buttonTextColor,
                        border: `2px solid ${style.buttonBackgroundColor}`,
                    }}
                >
                    {/* Shine effect */}
                    <div className="absolute inset-0" style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        transform: 'skewX(-12deg) translateX(-100%)',
                        transition: 'transform 1s',
                    }}></div>
                    <span className="relative flex items-center justify-center gap-3">
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Envoi en cours...
                            </>
                        ) : (
                            <>
                                {style.ctaText}
                                <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </>
                        )}
                    </span>
                </button>
            </div>

            <style>{`
                @keyframes slideInField {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
                    20%, 40%, 60%, 80% { transform: translateX(4px); }
                }
                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(0.5);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                .animate-shake {
                    animation: shake 0.5s ease-in-out;
                }
                .animate-scale-in {
                    animation: scaleIn 0.3s ease-out;
                }
            `}</style>
        </form>
    );
};

export default LeadForm;
